import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import {
  Box,
  Heading,
  Button,
  Flex,
  Text,
  Spinner,
  IconButton,
} from "@radix-ui/themes";
import {
  MagnifyingGlassIcon,
  CheckIcon,
  Cross2Icon,
} from "@radix-ui/react-icons";
import type { FetchResult, SearchParams, SortParams } from "./types";
import { ManagerContext, type ManagerContextValue } from "./context";
import { useDebounce } from "./hooks";
import { CreateButton } from "./CreateButton";
import { Search } from "./Search";
import { Sort, type SortProps } from "./Sort";
import { DataTable } from "./DataTable";
import { FormDialog } from "./FormDialog";
import { DeleteDialog } from "./DeleteDialog";

type RootProps<T> = {
  title: string;
  fetchData: (
    limit?: number,
    offset?: number,
    searchParams?: SearchParams,
    sortParams?: SortParams,
  ) => Promise<FetchResult<T>>;
  limit?: number;
  allowSelection?: boolean;
  getItemId?: (item: T) => string | number;
  fetchAllIds?: (searchParams?: SearchParams) => Promise<(string | number)[]>;
  actionButtons?: ReactNode;
  children: ReactNode;
};

export function Root<T>({
  title,
  fetchData,
  limit = 10,
  allowSelection = false,
  getItemId,
  fetchAllIds,
  actionButtons,
  children,
}: RootProps<T>) {
  const [data, setData] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchParams>({});
  const [sortParams, setSortParams] = useState<SortParams | null>(null);
  const [sortableColumns, setSortableColumns] = useState<Set<string>>(
    new Set(),
  );
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(
    new Set(),
  );
  const defaultSortRef = useRef<SortParams | null>(null);

  const debouncedSearchText = useDebounce(searchParams.text, 300);
  const debouncedBinarySearch = useDebounce(searchParams.binary, 300);
  const debouncedFilters = useDebounce(searchParams.filters, 300);
  const prevSearchRef = useRef<{
    text?: string;
    binary?: Record<string, "yes" | "no" | undefined>;
    filters?: Record<string, unknown>;
  }>({ text: undefined, binary: undefined, filters: undefined });

  const isEditMode = selectedItem !== null && dialogOpen;

  const setFormValue = useCallback((name: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const openCreate = useCallback(() => {
    setSelectedItem(null);
    setFormValues({});
    setError(null);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((item: T) => {
    setSelectedItem(item);
    setError(null);
    setDialogOpen(true);
  }, []);

  const openDelete = useCallback((item: T) => {
    setSelectedItem(item);
    setError(null);
    setDeleteDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedItem(null);
    setFormValues({});
    setError(null);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedItem(null);
    setError(null);
  }, []);

  const toggleSearch = useCallback(() => {
    setSearchOpen((prev) => !prev);
  }, []);

  const setSearchText = useCallback((text: string) => {
    setSearchParams((prev) => ({ ...prev, text: text || undefined }));
  }, []);

  const setBinarySearch = useCallback(
    (key: string, value: "yes" | "no" | undefined) => {
      setSearchParams((prev) => ({
        ...prev,
        binary: { ...prev.binary, [key]: value },
      }));
    },
    [],
  );

  const setFilter = useCallback((key: string, value: unknown) => {
    setSearchParams((prev) => ({
      ...prev,
      filters: { ...prev.filters, [key]: value },
    }));
  }, []);

  const setSort = useCallback((column: string) => {
    setSortParams((prev) => {
      if (prev?.column === column) {
        // Toggle order
        return { column, order: prev.order === "asc" ? "desc" : "asc" };
      }
      // New column, default to ascending
      return { column, order: "asc" };
    });
  }, []);

  const registerSortableColumn = useCallback((column: string) => {
    setSortableColumns((prev) => {
      if (prev.has(column)) return prev;
      const next = new Set(prev);
      next.add(column);
      return next;
    });
  }, []);

  // Selection helpers
  const getIdFromItem = useCallback(
    (item: T): string | number => {
      if (getItemId) return getItemId(item);
      return (item as { id?: string | number }).id ?? 0;
    },
    [getItemId],
  );

  const toggleSelectAll = useCallback(async () => {
    // If all are selected, clear. Otherwise select all.
    const total = totalCount ?? data.length;
    if (selectedIds.size === total && total > 0) {
      setSelectedIds(new Set());
    } else if (fetchAllIds) {
      // Fetch all IDs across all pages
      try {
        const allIds = await fetchAllIds(searchParams);
        setSelectedIds(new Set(allIds));
      } catch (err) {
        console.error("Failed to fetch all IDs for selection:", err);
      }
    } else {
      // Fallback: only select current page items
      setSelectedIds(new Set(data.map(getIdFromItem)));
    }
  }, [
    data,
    selectedIds.size,
    getIdFromItem,
    totalCount,
    fetchAllIds,
    searchParams,
  ]);

  const toggleSelectItem = useCallback((id: string | number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const allSelected =
    (totalCount ?? data.length) > 0 &&
    selectedIds.size === (totalCount ?? data.length);
  const someSelected =
    selectedIds.size > 0 && selectedIds.size < (totalCount ?? data.length);
  const selectionCount = selectedIds.size;

  const refresh = useCallback(
    async (
      page: number,
      effectiveSearchParams?: SearchParams,
      effectiveSortParams?: SortParams | null,
    ) => {
      try {
        setLoading(true);
        const offset = (page - 1) * limit;
        const result = await fetchData(
          limit,
          offset,
          effectiveSearchParams ?? {},
          effectiveSortParams ?? undefined,
        );
        setData(result.data);
        setHasMore(result.hasMore);
        setTotalCount(result.totalCount ?? null);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, limit],
  );

  const debouncedSearchParams = useMemo(
    () => ({
      text: debouncedSearchText,
      binary: debouncedBinarySearch,
      filters: debouncedFilters,
    }),
    [debouncedSearchText, debouncedBinarySearch, debouncedFilters],
  );

  const goToNextPage = useCallback(() => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    refresh(nextPage, debouncedSearchParams, sortParams);
  }, [currentPage, refresh, debouncedSearchParams, sortParams]);

  const goToPrevPage = useCallback(() => {
    const prevPage = currentPage - 1;
    setCurrentPage(prevPage);
    refresh(prevPage, debouncedSearchParams, sortParams);
  }, [currentPage, refresh, debouncedSearchParams, sortParams]);

  // Initial fetch on mount
  useEffect(() => {
    // Wait for default sort to be set by Sort component
    const timer = setTimeout(() => {
      refresh(1, {}, defaultSortRef.current);
      if (defaultSortRef.current && !sortParams) {
        setSortParams(defaultSortRef.current);
      }
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh when debounced search params change
  useEffect(() => {
    const prevText = prevSearchRef.current.text;
    const prevBinary = prevSearchRef.current.binary;
    const prevFilters = prevSearchRef.current.filters;
    const textChanged = prevText !== debouncedSearchText;
    const binaryChanged =
      JSON.stringify(prevBinary) !== JSON.stringify(debouncedBinarySearch);
    const filtersChanged =
      JSON.stringify(prevFilters) !== JSON.stringify(debouncedFilters);

    if (textChanged || binaryChanged || filtersChanged) {
      prevSearchRef.current = {
        text: debouncedSearchText,
        binary: debouncedBinarySearch,
        filters: debouncedFilters,
      };
      setCurrentPage(1);
      refresh(1, debouncedSearchParams, sortParams);
    }
  }, [
    debouncedSearchText,
    debouncedBinarySearch,
    debouncedFilters,
    debouncedSearchParams,
    sortParams,
    refresh,
  ]);

  // Refresh when sort params change
  useEffect(() => {
    if (sortParams) {
      setCurrentPage(1);
      refresh(1, debouncedSearchParams, sortParams);
    }
  }, [sortParams, debouncedSearchParams, refresh]);

  // Separate children by type and extract Sort config
  const createButton: ReactNode[] = [];
  const tableContent: ReactNode[] = [];
  const dialogs: ReactNode[] = [];
  let searchContent: ReactNode = null;
  let sortConfig: SortProps | null = null;

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === CreateButton) {
        createButton.push(child);
      } else if (child.type === DataTable) {
        tableContent.push(child);
      } else if (child.type === FormDialog || child.type === DeleteDialog) {
        dialogs.push(child);
      } else if (
        child.type === Search ||
        (child.type as React.FC & { displayName?: string })?.displayName ===
          "ManagerSearch"
      ) {
        searchContent = child;
      } else if (child.type === Sort) {
        sortConfig = child.props as SortProps;
      } else {
        tableContent.push(child);
      }
    }
  });

  // Register sortable columns from Sort config
  useEffect(() => {
    if (sortConfig) {
      sortConfig.columns.forEach((col) => {
        registerSortableColumn(col.key);
      });
    }
  }, [sortConfig, registerSortableColumn]);

  // Set default sort on initial mount
  useEffect(() => {
    if (sortConfig) {
      const defaultCol = sortConfig.columns.find((col) => col.isDefault);
      if (defaultCol) {
        const defaultSort: SortParams = {
          column: defaultCol.key,
          order: defaultCol.defaultOrder ?? "asc",
        };
        defaultSortRef.current = defaultSort;
        setSortParams(defaultSort);
      }
    }
  }, [sortConfig]);

  const hasSearch = searchContent !== null;

  const contextValue = useMemo(
    () => ({
      data,
      hasMore,
      loading,
      currentPage,
      totalCount,
      limit,
      selectedItem,
      isEditMode,
      dialogOpen,
      deleteDialogOpen,
      submitting,
      deleting,
      error,
      formValues,
      searchOpen,
      searchParams,
      hasSearch,
      sortParams,
      sortableColumns,
      setFormValue,
      openCreate,
      openEdit,
      openDelete,
      closeDialog,
      closeDeleteDialog,
      setSubmitting,
      setDeleting,
      setError,
      refresh: () => refresh(currentPage, debouncedSearchParams, sortParams),
      toggleSearch,
      setSearchText,
      setBinarySearch,
      setFilter,
      setSort,
      registerSortableColumn,
      // Selection
      allowSelection,
      selectedIds,
      allSelected,
      someSelected,
      selectionCount,
      toggleSelectAll,
      toggleSelectItem,
      clearSelection,
    }),
    [
      data,
      hasMore,
      loading,
      currentPage,
      limit,
      totalCount,
      selectedItem,
      isEditMode,
      dialogOpen,
      deleteDialogOpen,
      submitting,
      deleting,
      error,
      formValues,
      searchOpen,
      searchParams,
      hasSearch,
      sortParams,
      sortableColumns,
      setFormValue,
      openCreate,
      openEdit,
      openDelete,
      closeDialog,
      closeDeleteDialog,
      refresh,
      debouncedSearchParams,
      toggleSearch,
      setSearchText,
      setBinarySearch,
      setFilter,
      setSort,
      registerSortableColumn,
      // Selection
      allowSelection,
      selectedIds,
      allSelected,
      someSelected,
      selectionCount,
      toggleSelectAll,
      toggleSelectItem,
      clearSelection,
    ],
  );

  return (
    <ManagerContext.Provider
      value={contextValue as unknown as ManagerContextValue<never>}
    >
      <Flex gap="4">
        {/* Main content area */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          {/* Top bar: Title + Selection | Create + Actions + Search */}
          <Flex justify="between" align="center" mb="4">
            <Flex align="center" gap="3">
              <Heading size="7">{title}</Heading>
              {allowSelection && (totalCount ?? data.length) > 0 && (
                <>
                  <Button variant="soft" size="2" onClick={toggleSelectAll}>
                    {allSelected ? (
                      <>
                        <Cross2Icon />
                        Clear Selection
                      </>
                    ) : (
                      <>
                        <CheckIcon />
                        Select All
                      </>
                    )}
                  </Button>
                  {selectionCount > 0 && (
                    <Text size="2" color="gray">
                      {selectionCount} of {totalCount ?? data.length} selected
                    </Text>
                  )}
                </>
              )}
            </Flex>
            <Flex align="center" gap="2">
              {createButton}
              {actionButtons}
              {hasSearch && (
                <IconButton
                  variant={searchOpen ? "solid" : "soft"}
                  onClick={toggleSearch}
                  title="Search"
                >
                  <MagnifyingGlassIcon />
                </IconButton>
              )}
            </Flex>
          </Flex>

          {loading && data.length === 0 ? (
            <Flex justify="center" py="6">
              <Spinner size="3" />
            </Flex>
          ) : (
            <>
              {tableContent}
              <Flex justify="end" align="center" gap="3" mt="3">
                {totalCount !== null && (
                  <Text size="2" color="gray">
                    Showing {data.length} {data.length === 1 ? "item" : "items"}{" "}
                    out of {totalCount}
                  </Text>
                )}
                {(currentPage > 1 || hasMore) && (
                  <Flex gap="2">
                    {currentPage > 1 && (
                      <Button
                        variant="soft"
                        onClick={goToPrevPage}
                        disabled={loading}
                      >
                        Back
                      </Button>
                    )}
                    {hasMore && (
                      <Button
                        variant="soft"
                        onClick={goToNextPage}
                        disabled={loading}
                      >
                        Next
                      </Button>
                    )}
                  </Flex>
                )}
              </Flex>
            </>
          )}
        </Box>
        {/* Search panel */}
        {searchOpen && searchContent}
      </Flex>
      {dialogs}
    </ManagerContext.Provider>
  );
}
